const SELECTORS = {
  track: "[data-product-family-track]",
  card: ".product-family__card",
  media: ".product-family__media",
  previous: "[data-prev]",
  next: "[data-next]",
};

const CLASSES = {
  hidden: "hidden",
  navigationHidden: "carousel-navigation--hidden",
  navigationHiddenDesktop: "carousel-navigation--hidden-desktop",
  draggable: "is-draggable",
  dragging: "is-dragging",
  noTransition: "no-transition",
};

const DRAG_THRESHOLD = 5;
const DRAG_RELEASE_DELAY = 100;

class ProductFamilyCarousel extends HTMLElement {
  private track: HTMLElement | null = null;
  private previousButton: HTMLButtonElement | null = null;
  private nextButton: HTMLButtonElement | null = null;
  private links: HTMLAnchorElement[] = [];
  private events: (() => void)[] = [];
  private resizeObserver: ResizeObserver | null = null;
  private scrollFrame: number | null = null;
  private releaseTimeout: number | null = null;
  private pointerId: number | null = null;
  private pointerStartX = 0;
  private pointerStartScroll = 0;
  private isDragging = false;
  private suppressClick = false;

  connectedCallback(): void {
    this.track = this.querySelector<HTMLElement>(SELECTORS.track);
    this.previousButton = this.querySelector<HTMLButtonElement>(
      SELECTORS.previous,
    );
    this.nextButton = this.querySelector<HTMLButtonElement>(SELECTORS.next);

    if (!this.track) return;

    this.links = Array.from(
      this.track.querySelectorAll<HTMLAnchorElement>("a[href]"),
    );

    this.bindNavigation();
    this.bindDrag();
    this.bindScroll();
    this.observeSize();
    this.refresh();
  }

  disconnectedCallback(): void {
    this.events.forEach((unsubscribe) => unsubscribe());
    this.events = [];

    this.resizeObserver?.disconnect();
    this.resizeObserver = null;

    if (this.scrollFrame !== null) {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
    }

    if (this.releaseTimeout !== null) {
      window.clearTimeout(this.releaseTimeout);
      this.releaseTimeout = null;
    }
  }

  private bindNavigation(): void {
    const controls: [HTMLButtonElement | null, number][] = [
      [this.previousButton, -1],
      [this.nextButton, 1],
    ];

    controls.forEach(([button, direction]) => {
      if (!button) return;

      const clickHandler = () => this.scrollByStep(direction);
      button.addEventListener("click", clickHandler);
      this.events.push(() => button.removeEventListener("click", clickHandler));
    });
  }

  private bindDrag(): void {
    const track = this.track;
    if (!track) return;

    const pointerDownHandler = (event: PointerEvent) => {
      if (event.pointerType === "touch" || event.button !== 0) return;

      this.pointerId = event.pointerId;
      this.pointerStartX = event.clientX;
      this.pointerStartScroll = track.scrollLeft;
      this.isDragging = false;
      this.suppressClick = false;
    };

    const pointerMoveHandler = (event: PointerEvent) => {
      if (this.pointerId === null || event.pointerId !== this.pointerId) return;

      const delta = event.clientX - this.pointerStartX;

      if (!this.isDragging) {
        if (Math.abs(delta) < DRAG_THRESHOLD) return;
        this.startDrag();
      }

      event.preventDefault();
      track.scrollLeft = this.pointerStartScroll - delta;
    };

    const pointerUpHandler = (event: PointerEvent) => {
      if (this.pointerId === null || event.pointerId !== this.pointerId) return;

      this.pointerId = null;
      if (this.isDragging) this.endDrag();
    };

    const clickCaptureHandler = (event: MouseEvent) => {
      if (!this.suppressClick) return;

      event.preventDefault();
      event.stopPropagation();
    };

    const dragStartHandler = (event: Event) => {
      if (this.pointerId !== null) event.preventDefault();
    };

    track.addEventListener("pointerdown", pointerDownHandler);
    track.addEventListener("click", clickCaptureHandler, true);
    track.addEventListener("dragstart", dragStartHandler);
    document.addEventListener("pointermove", pointerMoveHandler);
    document.addEventListener("pointerup", pointerUpHandler);
    document.addEventListener("pointercancel", pointerUpHandler);

    this.events.push(() => {
      track.removeEventListener("pointerdown", pointerDownHandler);
      track.removeEventListener("click", clickCaptureHandler, true);
      track.removeEventListener("dragstart", dragStartHandler);
      document.removeEventListener("pointermove", pointerMoveHandler);
      document.removeEventListener("pointerup", pointerUpHandler);
      document.removeEventListener("pointercancel", pointerUpHandler);
    });
  }

  private bindScroll(): void {
    const track = this.track;
    if (!track) return;

    const scrollHandler = () => {
      if (this.scrollFrame !== null) return;

      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateScrollState();
      });
    };

    track.addEventListener("scroll", scrollHandler);
    this.events.push(() => track.removeEventListener("scroll", scrollHandler));
  }

  private observeSize(): void {
    if (!this.track || typeof ResizeObserver === "undefined") return;

    this.resizeObserver = new ResizeObserver(() => this.refresh());
    this.resizeObserver.observe(this.track);

    const media = this.querySelector<HTMLElement>(SELECTORS.media);
    if (media) this.resizeObserver.observe(media);
  }

  private refresh(): void {
    this.updateOverflow();
    this.updateNavigationPosition();
    this.updateScrollState();
  }

  private isOverflowing(): boolean {
    if (!this.track) return false;

    return this.track.scrollWidth - this.track.clientWidth > 1;
  }

  private updateOverflow(): void {
    const overflowing = this.isOverflowing();

    this.track?.classList.toggle(CLASSES.draggable, overflowing);

    [this.previousButton, this.nextButton].forEach((button) => {
      if (!button) return;

      button.classList.remove(CLASSES.navigationHiddenDesktop);
      button.classList.toggle(CLASSES.navigationHidden, !overflowing);
    });
  }

  private updateNavigationPosition(): void {
    this.previousButton?.classList.remove(CLASSES.hidden);
    this.nextButton?.classList.remove(CLASSES.hidden);

    const media = this.querySelector<HTMLElement>(SELECTORS.media);
    if (!media) return;

    const buttonHeight =
      this.nextButton?.clientHeight || this.previousButton?.clientHeight || 0;
    if (!buttonHeight) return;

    const offset = media.clientHeight / 2 - buttonHeight / 2;
    this.style.setProperty("--navigation-offset", `${offset}px`);
  }

  private updateScrollState(): void {
    const track = this.track;
    if (!track) return;

    const maxScroll = track.scrollWidth - track.clientWidth;

    if (this.previousButton) {
      this.previousButton.disabled = track.scrollLeft <= 1;
    }

    if (this.nextButton) {
      this.nextButton.disabled = track.scrollLeft >= maxScroll - 1;
    }
  }

  private getStep(): number {
    const track = this.track;
    if (!track) return 0;

    const cards = track.querySelectorAll<HTMLElement>(SELECTORS.card);

    if (cards.length > 1) {
      return cards[1].offsetLeft - cards[0].offsetLeft;
    }

    return cards[0] ? cards[0].offsetWidth : track.clientWidth;
  }

  private scrollByStep(direction: number): void {
    if (!this.track) return;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    this.track.scrollBy({
      left: this.getStep() * direction,
      behavior: reduceMotion ? "auto" : "smooth",
    });
  }

  private startDrag(): void {
    this.isDragging = true;
    this.suppressClick = true;
    this.track?.classList.add(CLASSES.dragging);
    this.links.forEach((link) => link.classList.add(CLASSES.noTransition));
  }

  private endDrag(): void {
    this.isDragging = false;
    this.track?.classList.remove(CLASSES.dragging);

    if (this.releaseTimeout !== null) {
      window.clearTimeout(this.releaseTimeout);
    }

    this.releaseTimeout = window.setTimeout(() => {
      this.releaseTimeout = null;
      this.suppressClick = false;
      this.links.forEach((link) => link.classList.remove(CLASSES.noTransition));
    }, DRAG_RELEASE_DELAY);
  }
}

if (!customElements.get("product-family-carousel")) {
  customElements.define("product-family-carousel", ProductFamilyCarousel);
}

export default ProductFamilyCarousel;
